import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Radio,
  Button,
  Row,
  Col,
  Popover,
  Checkbox,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { CheckSquare, Edit3, Trash2 } from "lucide-react";
import { ci_list } from "../Configurations/checklistApi";
import useAccentTheme from "@/hooks/useAccentTheme";
import { defaultColors } from "@/themes/antd-theme";

const { TextArea } = Input;

const validationSchema = Yup.object().shape({
  checklist_label: Yup.string().nullable().required("Label is required"),
  // mode: Yup.string().nullable().required("Choose Auto/Manual"),
  // device_id: Yup.string()
  //   .nullable()
  //   .when("mode", {
  //     is: "auto",
  //     then: (schema) => schema.required("Device is required in Auto mode"),
  //   }),
});

export default function Checklist({
  editIndex,
  onSubmit = (values) => console.log("Submitted values:", values),
  device_list,
  values = {
    checklist_label: null,
    // mode: null,
    // device_id: null,
  },
}) {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Checklist"
                validateStatus={
                  touched.checklist_label && errors.checklist_label
                    ? "error"
                    : ""
                }
                help={touched.checklist_label && errors.checklist_label}
              >
                <TextArea
                  rows={2}
                  placeholder="Label for the checklist"
                  value={values.checklist_label}
                  onChange={(e) =>
                    setFieldValue("checklist_label", e.target.value)
                  }
                />
              </Form.Item>
            </Col>

            {/* <Col span={4}>
              <Form.Item
                label="Mode"
                validateStatus={touched.mode && errors.mode ? "error" : ""}
                help={touched.mode && errors.mode}
                tooltip="Choose either 'Auto' or 'Manual' as the operation type"
              >
                <Radio.Group
                  value={values.mode}
                  onChange={(e) => setFieldValue("mode", e.target.value)}
                >
                  <Radio value="auto">Auto</Radio>
                  <Radio value="manual">Manual</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            {values.mode === "auto" && (
              <Col span={8}>
                <Form.Item
                  label="Select Device"
                  validateStatus={
                    touched.device_id && errors.device_id ? "error" : ""
                  }
                  help={touched.device_id && errors.device_id}
                >
                  <Space.Compact className="w-full">
                    <Select
                      allowClear
                      showSearch
                      value={values.device_id}
                      placeholder="Select Device"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(val) =>
                        setFieldValue("device_id", val || null)
                      }
                      options={device_list?.map((d) => ({
                        value: d.device_id,
                        label: d.device_name,
                      }))}
                    />
                    <Tooltip title="Add Device">
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                          // setModalVisible(true);
                        }}
                      />
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>
              </Col>
            )} */}

            {/* Submit */}
            <Col span={24}>
              <Button type="primary" htmlType="submit" block>
                {editIndex !== null ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

export const ChecklistCard = ({ item, onEdit, onDelete, fromView }) => {
  // item.checklist_items is an array of checklist-field IDs (see the form
  // above) — resolve them to their labels for display by fetching the same
  // master list independently, since this card is rendered generically by
  // the parent (SubOperationDetails.js) with no extra props to piggyback on.
  const [fieldNameById, setFieldNameById] = useState({});
  const { isDark, accentColor, accentBgSoft } = useAccentTheme();

  // Dark mode colors
  const cardBg = isDark ? defaultColors.palette.gray[800] : "#ffffff";
  const borderColor = isDark ? defaultColors.palette.gray[600] : "#e5e7eb";
  const textColor = isDark ? "#ffffff" : "#111827";

  useEffect(() => {
    ci_list((res) => {
      if (res?.data?.status === "success") {
        const map = {};
        (res?.data?.data || []).forEach((f) => {
          map[f.id] = f.name;
        });
        setFieldNameById(map);
      }
    });
  }, []);

  return (
    <div
      className="rounded-lg border shadow-sm hover:shadow-md transition p-3 text-sm"
      style={{ backgroundColor: cardBg, borderColor }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBgSoft }}>
            <CheckSquare className="w-4 h-4" style={{ color: accentColor }} />
          </div>
          <p
            className="leading-tight text-base break-all"
            style={{ color: textColor }}
            title={item.checklist_label}
          >
            {item.checklist_label}
          </p>
        </div>

       {!fromView && <div className="flex items-center gap-1">
          {onEdit && (
            <button
              type="button"
              className="p-1.5 rounded-md transition-colors border border-transparent"
              onClick={() => onEdit()}
              title="Edit checklist"
            >
              <Edit3 className="w-4 h-4" style={{ color: accentColor }} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="p-1.5 rounded-md transition-colors border border-transparent"
              onClick={() => onDelete()}
              title="Delete checklist"
            >
              <Trash2 className="w-4 h-4" style={{ color: accentColor }} />
            </button>
          )}
        </div>}
      </div>

      {/* Read-only preview of the checklist items as actual checkboxes —
          this is what the operator will eventually tick off during batch
          execution; here it's just a static preview of what was configured. */}
      {item.checklist_items?.length > 0 && (
        <div className="mt-2 pl-1 flex flex-col gap-1">
          {item.checklist_items.map((id, idx) => (
            <Checkbox key={idx} disabled>
              <span style={{ color: textColor }}>{fieldNameById[id] || id}</span>
            </Checkbox>
          ))}
        </div>
      )}
    </div>
  );
};
